import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { loadQuotaPrefs, type QuotaNotificationPrefs } from "@/lib/quotaNotificationPrefs";

const STREAK_MILESTONES = [7, 14, 30, 50, 100, 200, 365];

/**
 * Human-readable time until the daily scan quota resets (midnight UTC).
 */
export function timeUntilQuotaReset(now: Date = new Date()): { ms: number; label: string } {
  const reset = new Date(now);
  reset.setUTCHours(24, 0, 0, 0);
  const ms = reset.getTime() - now.getTime();
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.round((ms % 3_600_000) / 60_000);
  const label =
    hours >= 1
      ? `${hours}h ${minutes}m`
      : `${Math.max(1, minutes)} min${minutes === 1 ? "" : "s"}`;
  return { ms, label };
}

export function useInAppNotifications() {
  const { user, streak, cancelAtPeriodEnd, currentPeriodEnd, isPaid, scanCount, scanLimit } = useAuth();
  const hasShownRef = useRef<Set<string>>(new Set());
  const [prefs, setPrefs] = useState<QuotaNotificationPrefs>(() => loadQuotaPrefs());

  // Pick up changes made on the usage page without a reload.
  useEffect(() => {
    const sync = () => setPrefs(loadQuotaPrefs());
    window.addEventListener("inquira:quota-prefs-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("inquira:quota-prefs-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    const sessionKey = `notifs-${user.id}-${new Date().toISOString().split("T")[0]}`;
    const shown = hasShownRef.current;

    // Streak milestone celebration
    if (streak > 0 && STREAK_MILESTONES.includes(streak) && !shown.has(`streak-${streak}`)) {
      shown.add(`streak-${streak}`);
      const stored = localStorage.getItem(`mimi-streak-celebrated-${streak}`);
      if (!stored) {
        localStorage.setItem(`mimi-streak-celebrated-${streak}`, "true");
        setTimeout(() => {
          toast.success(`🔥 ${streak}-day streak!`, {
            description: streak >= 100
              ? "Incredible dedication! You're a true learner."
              : streak >= 30
                ? "A whole month of consistent studying! Amazing!"
                : streak >= 7
                  ? "One week strong! Keep the momentum going."
                  : `${streak} days in a row! Keep it up!`,
            duration: 6000,
          });
        }, 1500);
      }
    }

    // Subscription expiry warning
    if (isPaid && cancelAtPeriodEnd && currentPeriodEnd && !shown.has("sub-expiry")) {
      shown.add("sub-expiry");
      const endDate = new Date(currentPeriodEnd);
      const now = new Date();
      const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysLeft <= 7 && daysLeft > 0) {
        const lastWarning = localStorage.getItem("mimi-sub-expiry-warned");
        const today = now.toISOString().split("T")[0];
        if (lastWarning !== today) {
          localStorage.setItem("mimi-sub-expiry-warned", today);
          setTimeout(() => {
            toast.warning(`Pro expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`, {
              description: "Renew your subscription to keep your Premium benefits and practice mode.",
              duration: 8000,
              action: {
                label: "Renew",
                onClick: () => { window.location.href = "/upgrade"; },
              },
            });
          }, 3000);
        }
      }
    }
  }, [user, streak, cancelAtPeriodEnd, currentPeriodEnd, isPaid]);

  // Low scan-quota warning (free users only), with time until the daily reset.
  useEffect(() => {
    if (!user || isPaid) return;
    if (!prefs.enabled) return;
    const limit = Number.isFinite(scanLimit) ? scanLimit : 3;
    const remaining = Math.max(0, limit - scanCount);
    if (remaining > prefs.threshold) return;

    // Respect the user's cooldown between toasts.
    const key = `inquira-quota-warned-${user.id}`;
    const last = Number(localStorage.getItem(key) ?? 0);
    if (last && Date.now() - last < prefs.cooldownHours * 3_600_000) return;
    localStorage.setItem(key, String(Date.now()));

    const { label } = timeUntilQuotaReset();
    const title =
      remaining === 0
        ? "Daily AI questions used up"
        : `${remaining} AI question${remaining === 1 ? "" : "s"} left today`;
    toast.warning(title, {
      description:
        remaining === 0
          ? `Your ${limit} free AI questions reset in ${label}. Compare Premium study access on the Plans page.`
          : `You have ${remaining} of ${limit} free AI questions left. Your quota resets in ${label}.`,
      duration: 9000,
      action: { label: "See details", onClick: () => { window.location.href = "/usage"; } },
    });
  }, [user, isPaid, scanCount, scanLimit, prefs]);
}
